import { Link } from "react-router-dom";
import type { ActionLink as ActionLinkModel } from "@/types/site";

interface ActionLinkProps {
  action: ActionLinkModel;
  variant?: "primary" | "secondary" | "ghost";
}

function getVariantClass(variant: NonNullable<ActionLinkProps["variant"]>) {
  switch (variant) {
    case "secondary":
      return "border border-outline/20 bg-surface-high text-text hover:bg-surface-contrast";
    case "ghost":
      return "border border-transparent bg-transparent text-text hover:border-outline/20 hover:bg-surface/70";
    case "primary":
    default:
      return "bg-truth-gradient text-slate-950 hover:brightness-110";
  }
}

export function ActionLink({ action, variant = "primary" }: ActionLinkProps) {
  const classes = [
    "inline-flex items-center justify-center rounded-xl px-5 py-3 text-xs font-semibold uppercase tracking-[0.24em] transition-all duration-200",
    getVariantClass(variant),
    action.state === "placeholder" ? "cursor-not-allowed opacity-60" : "active:scale-[0.98]",
  ].join(" ");

  if (!action.href || action.state === "placeholder") {
    return (
      <span className={classes} aria-disabled="true" title={action.note}>
        {action.label}
      </span>
    );
  }

  if (action.href.startsWith("/")) {
    return (
      <Link className={classes} to={action.href}>
        {action.label}
      </Link>
    );
  }

  if (action.href.startsWith("#")) {
    return (
      <a className={classes} href={action.href}>
        {action.label}
      </a>
    );
  }

  return (
    <a className={classes} href={action.href} rel="noreferrer" target="_blank">
      {action.label}
    </a>
  );
}
